import React from "react";
import { useNavigate } from "react-router-dom";
import {
  MdCameraAlt,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdDelete,
  MdNavigateBefore,
  MdNavigateNext,
  MdRefresh,
  MdWarning,
} from "react-icons/md";
import { useLanguage } from "context/LanguageContext";
import { decodeJwt, roleHomePath, toAppRole } from "utils/auth";
import { api } from "services/api";
import { authService } from "services/authService";
import { faceService } from "services/admin/faceService";

interface FirstLoginForm {
  full_name: string;
  phone: string;
  dob: string;
  gender: string;
  height: string;
  weight: string;
  certificate: string;
  achievement: string;
  bio: string;
  base_salary: string;
  parent_phone: string;
  medical_note: string;
  new_password: string;
  confirm_password: string;
}

interface PendingSelfie {
  previewUrl: string;
  file: File;
}

type SelfieStepStatus =
  | "pending"
  | "capturing"
  | "captured"
  | "uploading"
  | "done"
  | "error";

const INITIAL: FirstLoginForm = {
  full_name: "",
  phone: "",
  dob: "",
  gender: "",
  height: "",
  weight: "",
  certificate: "",
  achievement: "",
  bio: "",
  base_salary: "",
  parent_phone: "",
  medical_note: "",
  new_password: "",
  confirm_password: "",
};

const PROFILE_INPUT_CLASS =
  "w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted dark:placeholder-gray-400";

const SELFIE_LABELS = ["front", "left", "right"];
const SELFIE_TITLE_KEYS = [
  "firstLogin.selfieFront",
  "firstLogin.selfieLeft",
  "firstLogin.selfieRight",
];

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(",", 2);
  if (!header || !base64) {
    throw new Error("firstLogin.errorProcessPhoto");
  }

  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch?.[1] || "image/jpeg";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new File([bytes], filename, { type: mime });
}

async function uploadAvatar(file: File): Promise<string> {
  const token = sessionStorage.getItem("access_token");
  const form = new FormData();
  form.append("file", file);
  try {
    const response = await fetch(`${API_BASE_URL}/users/me/avatar`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.detail ?? "firstLogin.errorUploadAvatar");
    }
    const data = (await response.json()) as { avatar_url: string };
    return data.avatar_url;
  } catch (err) {
    if (
      err instanceof TypeError ||
      (err instanceof Error &&
        (err.message.includes("Load failed") ||
          err.message.includes("Failed to fetch")))
    ) {
      throw new Error("firstLogin.errorAvatarNetwork");
    }
    throw err;
  }
}

function StepBadge({
  index,
  active,
  done,
  title,
}: {
  index: number;
  active: boolean;
  done: boolean;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
          done
            ? "bg-green-500 text-white"
            : active
            ? "bg-brand-500 text-white"
            : "bg-gray-200 text-ink"
        }`}
      >
        {index}
      </div>
      <span
        className={`text-sm ${
          active
            ? "font-semibold text-navy-700 dark:text-white"
            : "text-ink-muted"
        }`}
      >
        {title}
      </span>
    </div>
  );
}

export default function FirstLogin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const SELFIE_TITLES = SELFIE_TITLE_KEYS.map((key) => t(key));

  const [form, setForm] = React.useState<FirstLoginForm>(INITIAL);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [step, setStep] = React.useState(1);

  const [backendRole, setBackendRole] = React.useState<string>("");
  const [userId, setUserId] = React.useState<string>("");

  const [avatarFile, setAvatarFile] = React.useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = React.useState<string | null>(null);

  const [selfies, setSelfies] = React.useState<Record<number, PendingSelfie>>(
    {}
  );

  const fileRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Sequential selfie state
  const [selfieStep, setSelfieStep] = React.useState(0);
  const [selfieStatuses, setSelfieStatuses] = React.useState<
    SelfieStepStatus[]
  >(Array(3).fill("pending"));
  const [selfieCaptured, setSelfieCaptured] = React.useState<string | null>(
    null
  );
  const [selfieCameraError, setSelfieCameraError] = React.useState<
    string | null
  >(null);
  const [selfieError, setSelfieError] = React.useState<string | null>(null);
  const [selfieImageUrls, setSelfieImageUrls] = React.useState<
    Record<string, string>
  >({});
  const [selfieLoading, setSelfieLoading] = React.useState(false);
  const [selfieEmbeddings, setSelfieEmbeddings] = React.useState<
    { id: string; label: string }[]
  >([]);
  const selfieVideoRef = React.useRef<HTMLVideoElement>(null);
  const selfieCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const selfieStreamRef = React.useRef<MediaStream | null>(null);

  React.useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      navigate("/auth/sign-in", { replace: true });
      return;
    }

    const payload = decodeJwt(token);
    setBackendRole((payload?.role as string) ?? "");

    const hydrateUser = async () => {
      try {
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          setUserId(storedUser.id);
          setForm((prev) => ({
            ...prev,
            full_name: storedUser.full_name ?? prev.full_name,
            phone: storedUser.phone ?? prev.phone,
          }));
          return;
        }
        const me = await authService.getMe();
        setUserId(me.id);
        setForm((prev) => ({
          ...prev,
          full_name: me.full_name ?? prev.full_name,
          phone: me.phone ?? prev.phone,
        }));
      } catch {
        // Keep defaults.
      }
    };

    hydrateUser();
  }, [navigate]);

  React.useEffect(() => {
    return () => {
      if (avatarPreview) URL.revokeObjectURL(avatarPreview);
      Object.values(selfies).forEach((item) =>
        URL.revokeObjectURL(item.previewUrl)
      );
      selfieStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [avatarPreview, selfies]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const isTrainerFlow = ["MANAGER", "TRAINER", "TEACHER"].includes(backendRole);
  const isStudentFlow = backendRole === "STUDENT";
  const isManagerOnlyFlow = backendRole === "MANAGER";
  const totalSteps = isManagerOnlyFlow ? 2 : 3;

  // --- Sequential selfie camera functions ---
  const stopSelfieCamera = React.useCallback(() => {
    selfieStreamRef.current?.getTracks().forEach((t) => t.stop());
    selfieStreamRef.current = null;
  }, []);

  const startSelfieCamera = React.useCallback(async () => {
    setSelfieCameraError(null);
    setSelfieCaptured(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 640, height: 480 },
      });
      selfieStreamRef.current = stream;
      setTimeout(() => {
        if (selfieVideoRef.current) {
          selfieVideoRef.current.srcObject = stream;
          selfieVideoRef.current.play();
        }
      }, 100);
    } catch {
      setSelfieCameraError("firstLogin.cameraError");
    }
  }, []);

  // Load existing faces when entering step 3 (handles page refresh)
  const selfieLoadedRef = React.useRef(false);
  React.useEffect(() => {
    const selfieStepNumber = isManagerOnlyFlow ? 2 : 3;
    if (step !== selfieStepNumber || !userId || selfieLoadedRef.current) return;
    selfieLoadedRef.current = true;

    const loadExistingFaces = async () => {
      setSelfieLoading(true);
      try {
        const data = await faceService.list(userId);
        if (data.length === 0) {
          setSelfieLoading(false);
          return;
        }

        setSelfieEmbeddings(data.map((e) => ({ id: e.id, label: e.label })));

        const newStatuses: SelfieStepStatus[] = [...selfieStatuses];
        let firstPending = -1;
        for (let i = 0; i < 3; i++) {
          if (data.find((e) => e.label === SELFIE_LABELS[i])) {
            newStatuses[i] = "done";
          } else if (firstPending === -1) {
            firstPending = i;
          }
        }
        setSelfieStatuses(newStatuses);

        if (firstPending !== -1) {
          setSelfieStep(firstPending);
        }

        // Use presigned image URLs from API response
        const urls: Record<string, string> = {};
        for (const e of data) {
          if (SELFIE_LABELS.includes(e.label) && e.image_url) {
            urls[e.label] = e.image_url;
          }
        }
        setSelfieImageUrls(urls);
      } catch {
        // Silently continue — user can still capture
      } finally {
        setSelfieLoading(false);
      }
    };

    loadExistingFaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, userId, isManagerOnlyFlow]);

  // Auto-start camera when selfie step changes and we're on step 3
  React.useEffect(() => {
    const selfieStepNum = isManagerOnlyFlow ? 2 : 3;
    if (step !== selfieStepNum || selfieLoading) return;
    const status = selfieStatuses[selfieStep];
    if (status === "pending" || status === "capturing") {
      setSelfieStatuses((prev) => {
        const next = [...prev];
        next[selfieStep] = "capturing";
        return next;
      });
      stopSelfieCamera();
      startSelfieCamera();
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfieStep, step]);

  const captureSelfiePhoto = React.useCallback(() => {
    if (!selfieVideoRef.current || !selfieCanvasRef.current) return;
    const video = selfieVideoRef.current;
    const canvas = selfieCanvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    setSelfieCaptured(canvas.toDataURL("image/jpeg", 0.9));
    stopSelfieCamera();
    setSelfieStatuses((prev) => {
      const next = [...prev];
      next[selfieStep] = "captured";
      return next;
    });
  }, [stopSelfieCamera, selfieStep]);

  const retakeSelfiePhoto = React.useCallback(() => {
    setSelfieCaptured(null);
    setSelfieError(null);
    setSelfieStatuses((prev) => {
      const next = [...prev];
      next[selfieStep] = "capturing";
      return next;
    });
    startSelfieCamera();
  }, [startSelfieCamera, selfieStep]);

  const confirmAndUploadSelfie = React.useCallback(async () => {
    if (!selfieCaptured || !userId) return;
    setSelfieError(null);
    setSelfieStatuses((prev) => {
      const next = [...prev];
      next[selfieStep] = "uploading";
      return next;
    });

    try {
      const file = dataUrlToFile(
        selfieCaptured,
        `selfie_${SELFIE_LABELS[selfieStep]}.jpg`
      );
      await faceService.upload(userId, file, SELFIE_LABELS[selfieStep]);

      // Also store in selfies record for account activation flow
      setSelfies((prev) => ({
        ...prev,
        [selfieStep]: { previewUrl: URL.createObjectURL(file), file },
      }));

      // Refresh embeddings list and fetch the uploaded image
      try {
        const latest = await faceService.list(userId);
        setSelfieEmbeddings(latest.map((e) => ({ id: e.id, label: e.label })));

        // Use presigned image URL from re-fetched data
        const uploaded = latest.find(
          (e) => e.label === SELFIE_LABELS[selfieStep]
        );
        if (uploaded?.image_url) {
          setSelfieImageUrls((prev) => ({
            ...prev,
            [SELFIE_LABELS[selfieStep]]: uploaded.image_url!,
          }));
        }
      } catch {
        // Non-critical — list may fail but upload succeeded
      }

      setSelfieStatuses((prev) => {
        const next = [...prev];
        next[selfieStep] = "done";
        return next;
      });
      setSelfieCaptured(null);

      // Auto-advance to next pending step
      let nextPending = -1;
      for (let i = 0; i < 3; i++) {
        if (i !== selfieStep && selfieStatuses[i] !== "done") {
          nextPending = i;
          break;
        }
      }
      if (nextPending !== -1) {
        setSelfieStep(nextPending);
      } else {
        stopSelfieCamera();
      }
    } catch (e: unknown) {
      setSelfieError(
        e instanceof Error ? e.message : "firstLogin.uploadFailed"
      );
      setSelfieStatuses((prev) => {
        const next = [...prev];
        next[selfieStep] = "error";
        return next;
      });
    }
  }, [selfieCaptured, selfieStep, userId, selfieStatuses, stopSelfieCamera]);

  const handleDeleteAndRecaptureSelfie = React.useCallback(
    async (stepIndex: number) => {
      const label = SELFIE_LABELS[stepIndex];
      const embedding = selfieEmbeddings.find((e) => e.label === label);
      if (!embedding || !userId) return;

      setSelfieError(null);
      try {
        await faceService.delete(userId, embedding.id);

        // Refresh embeddings
        const latest = await faceService.list(userId);
        setSelfieEmbeddings(latest.map((e) => ({ id: e.id, label: e.label })));

        // Clear image URL for this label
        setSelfieImageUrls((prev) => {
          const next = { ...prev };
          delete next[label];
          return next;
        });

        // Clear local selfie preview
        setSelfies((prev) => {
          const next = { ...prev };
          if (next[stepIndex]?.previewUrl) {
            URL.revokeObjectURL(next[stepIndex].previewUrl);
          }
          delete next[stepIndex];
          return next;
        });

        setSelfieStatuses((prev) => {
          const next = [...prev];
          next[stepIndex] = "pending";
          return next;
        });
        setSelfieCaptured(null);
        setSelfieStep(stepIndex);
      } catch (e: unknown) {
        setSelfieError(
          e instanceof Error ? e.message : "firstLogin.deletePhotoFailed"
        );
      }
    },
    [selfieEmbeddings, userId]
  );

  const selfieEnrolledCount = selfieStatuses.filter((s) => s === "done").length;
  const allSelfiesDone = selfieEnrolledCount >= 3;

  const handlePickAvatar = (file: File) => {
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const validateStep1 = () => {
    // Allow skipping password change (both fields empty)
    if (!form.new_password && !form.confirm_password) return true;
    if (form.new_password !== form.confirm_password) {
      setError("changePassword.errorPasswordMismatch");
      return false;
    }
    if (form.new_password.length < 8) {
      setError("changePassword.errorPasswordTooShort");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.full_name.trim()) {
      setError("firstLogin.errorFullNameRequired");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (selfieStatuses.filter((s) => s === "done").length < 3) {
      setError("firstLogin.errorSelfiesRequired");
      return false;
    }
    return true;
  };

  const goNext = () => {
    setError(null);
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const goPrev = () => {
    setError(null);
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const activateAccount = async () => {
    setError(null);

    if (!validateStep1()) return;
    if (!isManagerOnlyFlow) {
      if (!validateStep2()) return;
    }
    if (!validateStep3()) return;

    setSubmitting(true);
    try {
      let avatarKey: string | undefined;
      if (!isManagerOnlyFlow && avatarFile) {
        avatarKey = await uploadAvatar(avatarFile);
      }

      // Selfies are already uploaded during the sequential capture flow in step 3.
      // No need to upload again here.

      await api.post("/users/me/complete-first-login", {
        new_password: form.new_password || undefined,
        full_name: !isManagerOnlyFlow ? form.full_name.trim() : undefined,
        phone: !isManagerOnlyFlow ? form.phone || undefined : undefined,
        dob: !isManagerOnlyFlow ? form.dob || undefined : undefined,
        gender: !isManagerOnlyFlow ? form.gender || undefined : undefined,
        height:
          !isManagerOnlyFlow && form.height ? Number(form.height) : undefined,
        weight:
          !isManagerOnlyFlow && form.weight ? Number(form.weight) : undefined,
        avatar_url: !isManagerOnlyFlow ? avatarKey || undefined : undefined,
        certificate:
          !isManagerOnlyFlow && isTrainerFlow
            ? form.certificate || undefined
            : undefined,
        achievement:
          !isManagerOnlyFlow && isTrainerFlow
            ? form.achievement || undefined
            : undefined,
        bio:
          !isManagerOnlyFlow && isTrainerFlow
            ? form.bio || undefined
            : undefined,
        base_salary:
          !isManagerOnlyFlow && isTrainerFlow && form.base_salary
            ? Number(form.base_salary)
            : undefined,
        parent_phone:
          !isManagerOnlyFlow && isStudentFlow
            ? form.parent_phone || undefined
            : undefined,
        medical_note:
          !isManagerOnlyFlow && isStudentFlow
            ? form.medical_note || undefined
            : undefined,
      });

      await authService.getMe().catch(() => {});

      const token = sessionStorage.getItem("access_token");
      const payload = token ? decodeJwt(token) : null;
      const appRole = toAppRole((payload?.role as string) ?? "");
      navigate(roleHomePath(appRole), { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "firstLogin.errorActivate");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-6">
      <div className="w-full max-w-4xl rounded-2xl bg-surface p-6 shadow-2xl md:p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-ink">
            {t("firstLogin.title")}
          </h2>
          <p className="mt-1 text-sm text-ink-muted">
            {isManagerOnlyFlow
              ? t("firstLogin.subtitleManager")
              : t("firstLogin.subtitle")}
          </p>
        </div>

        <div
          className={`mb-6 grid grid-cols-1 gap-3 rounded-xl border border-line bg-gray-50 p-4 dark:bg-surface-muted/30 ${ isManagerOnlyFlow ? "md:grid-cols-2" : "md:grid-cols-3"
          }`}
        >
          <StepBadge
            index={1}
            active={step === 1}
            done={step > 1}
            title={t("firstLogin.stepPassword")}
          />
          {!isManagerOnlyFlow && (
            <StepBadge
              index={2}
              active={step === 2}
              done={step > 2}
              title={t("firstLogin.stepProfile")}
            />
          )}
          <StepBadge
            index={isManagerOnlyFlow ? 2 : 3}
            active={step === totalSteps}
            done={false}
            title={
              isManagerOnlyFlow
                ? t("firstLogin.stepFace")
                : t("firstLogin.stepAvatarSelfie")
            }
          />
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-blue-700 dark:border-blue-900/30 dark:bg-blue-900/10 dark:text-blue-300">
              {t("firstLogin.passwordHint")}
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("changePassword.newPassword")}
              </label>
              <input
                name="new_password"
                type="password"
                value={form.new_password}
                onChange={handleChange}
                placeholder={t("firstLogin.newPasswordPlaceholder")}
                className={PROFILE_INPUT_CLASS}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("firstLogin.confirmPassword")}
              </label>
              <input
                name="confirm_password"
                type="password"
                value={form.confirm_password}
                onChange={handleChange}
                placeholder={t("changePassword.confirmPasswordPlaceholder")}
                className={PROFILE_INPUT_CLASS}
              />
            </div>
          </div>
        )}

        {!isManagerOnlyFlow && step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("firstLogin.fullName")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                required
                placeholder={t("firstLogin.fullNamePlaceholder")}
                className={PROFILE_INPUT_CLASS}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-ink">
                {t("settings.phone")}
              </label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="0901234567"
                className={PROFILE_INPUT_CLASS}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">
                  {t("settings.dob")}
                </label>
                <input
                  name="dob"
                  type="date"
                  value={form.dob}
                  onChange={handleChange}
                  className={PROFILE_INPUT_CLASS}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">
                  {t("settings.gender")}
                </label>
                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  className={PROFILE_INPUT_CLASS}
                >
                  <option value="">{t("firstLogin.selectGender")}</option>
                  <option value="MALE">{t("settings.male")}</option>
                  <option value="FEMALE">{t("settings.female")}</option>
                  <option value="OTHER">{t("settings.other")}</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">
                  {t("settings.height")}
                </label>
                <input
                  name="height"
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.height}
                  onChange={handleChange}
                  className={PROFILE_INPUT_CLASS}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">
                  {t("settings.weight")}
                </label>
                <input
                  name="weight"
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.weight}
                  onChange={handleChange}
                  className={PROFILE_INPUT_CLASS}
                />
              </div>
            </div>

            {isTrainerFlow && (
              <div className="space-y-4 rounded-xl border border-line p-4">
                <p className="text-sm font-semibold text-ink">
                  {t("firstLogin.staffProfile")}
                </p>
                <textarea
                  name="certificate"
                  value={form.certificate}
                  onChange={handleChange}
                  rows={2}
                  placeholder={t("settings.certificate")}
                  className={PROFILE_INPUT_CLASS}
                />
                <textarea
                  name="achievement"
                  value={form.achievement}
                  onChange={handleChange}
                  rows={2}
                  placeholder={t("settings.achievement")}
                  className={PROFILE_INPUT_CLASS}
                />
                <textarea
                  name="bio"
                  value={form.bio}
                  onChange={handleChange}
                  rows={3}
                  placeholder={t("firstLogin.bioPlaceholder")}
                  className={PROFILE_INPUT_CLASS}
                />
                <input
                  name="base_salary"
                  type="number"
                  min="0"
                  value={form.base_salary}
                  onChange={handleChange}
                  placeholder={t("firstLogin.baseSalaryPlaceholder")}
                  className={PROFILE_INPUT_CLASS}
                />
              </div>
            )}

            {isStudentFlow && (
              <div className="space-y-4 rounded-xl border border-line p-4">
                <p className="text-sm font-semibold text-ink">
                  {t("firstLogin.studentProfile")}
                </p>
                <input
                  name="parent_phone"
                  value={form.parent_phone}
                  onChange={handleChange}
                  placeholder={t("firstLogin.parentPhonePlaceholder")}
                  className={PROFILE_INPUT_CLASS}
                />
                <textarea
                  name="medical_note"
                  value={form.medical_note}
                  onChange={handleChange}
                  rows={3}
                  placeholder={t("firstLogin.medicalNotePlaceholder")}
                  className={PROFILE_INPUT_CLASS}
                />
              </div>
            )}

            <div className="rounded-xl border border-dashed border-brand-300 bg-brand-50 px-4 py-3 text-sm text-brand-700 dark:border-brand-400/40 dark:bg-brand-400/10 dark:text-brand-200">
              {t("firstLogin.nextStepHint")}
            </div>
          </div>
        )}

        {((isManagerOnlyFlow && step === 2) ||
          (!isManagerOnlyFlow && step === 3)) && (
          <div className="space-y-5">
            {!isManagerOnlyFlow && (
              <div className="rounded-xl border border-line p-4">
                <p className="mb-2 text-sm font-semibold text-ink">
                  {t("firstLogin.avatarOptional")}
                </p>
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                  <div className="h-20 w-20 overflow-hidden rounded-full border border-line bg-surface-muted">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt={t("firstLogin.avatarPreviewAlt")}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xs text-ink-subtle">
                        {t("firstLogin.noAvatar")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileRefs.current[10]?.click()}
                      className="rounded-lg border border-line px-3 py-2 text-sm text-ink hover:border-brand-500"
                    >
                      {t("firstLogin.chooseAvatar")}
                    </button>
                    {avatarPreview && (
                      <button
                        type="button"
                        onClick={() => {
                          if (avatarPreview) URL.revokeObjectURL(avatarPreview);
                          setAvatarPreview(null);
                          setAvatarFile(null);
                        }}
                        className="rounded-lg border border-red-200 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-300 dark:border-red-500/30"
                      >
                        {t("action.delete")}
                      </button>
                    )}
                  </div>
                  <input
                    ref={(el) => {
                      fileRefs.current[10] = el;
                    }}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePickAvatar(file);
                      e.target.value = "";
                    }}
                  />
                </div>
              </div>
            )}

            <div className="rounded-xl border border-line p-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-ink">
                    {t("firstLogin.selfiesTitle")}
                  </p>
                  <p className="text-xs text-ink-muted">
                    {t("firstLogin.selfiesHint")}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`text-sm font-semibold ${
                      allSelfiesDone ? "text-green-500" : "text-amber-500"
                    }`}
                  >
                    {selfieEnrolledCount}/3
                  </span>
                  {allSelfiesDone ? (
                    <MdCheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <MdWarning className="h-5 w-5 text-amber-500" />
                  )}
                </div>
              </div>

              {selfieLoading && (
                <div className="mb-4 flex items-center justify-center py-8">
                  <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
                </div>
              )}

              {!selfieLoading && (
                <>
                  {/* Progress bar */}
                  <div className="mb-4 h-1.5 w-full rounded-full bg-gray-200 dark:bg-surface-hover">
                    <div
                      className={`ui-progress-fill h-1.5 rounded-full bg-brand-500 transition-all duration-500 ui-progress-step-${Math.min(
                        selfieEnrolledCount,
                        3
                      )}`}
                    />
                  </div>

                  {/* Step indicators */}
                  <div className="mb-4 flex items-center justify-center gap-2">
                    {SELFIE_TITLES.map((title, i) => {
                      const status = selfieStatuses[i];
                      const isCurrent = i === selfieStep && !allSelfiesDone;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            if (status === "done" || status === "pending") {
                              stopSelfieCamera();
                              setSelfieCaptured(null);
                              setSelfieError(null);
                              setSelfieStep(i);
                            }
                          }}
                          className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-colors ${
                            isCurrent
                              ? "bg-brand-50 dark:bg-brand-900/20"
                              : "hover:bg-surface-hover"
                          }`}
                        >
                          <div
                            className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${
                              status === "done"
                                ? "bg-green-500 text-white"
                                : status === "error"
                                ? "bg-red-500 text-white"
                                : status === "uploading"
                                ? "animate-pulse bg-amber-400 text-white"
                                : isCurrent
                                ? "bg-brand-500 text-white"
                                : "bg-gray-200 text-ink-muted dark:bg-surface-hover"
                            }`}
                          >
                            {status === "done" ? (
                              <MdCheckCircle className="h-4 w-4" />
                            ) : (
                              i + 1
                            )}
                          </div>
                          <span
                            className={`${
                              isCurrent
                                ? "font-semibold text-brand-600 dark:text-brand-400"
                                : "text-ink-muted"
                            }`}
                          >
                            {title}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {allSelfiesDone ? (
                    /* All selfies complete */
                    <div className="flex flex-col items-center gap-3 rounded-xl bg-green-50 py-8 dark:bg-green-900/10">
                      <MdCheckCircle className="h-12 w-12 text-green-500" />
                      <p className="text-sm font-medium text-green-700 dark:text-green-400">
                        {t("firstLogin.selfiesDone")}
                      </p>
                      <div className="mt-1 flex gap-2">
                        {SELFIE_TITLES.map((title, i) => (
                          <div
                            key={i}
                            className="group relative flex flex-col items-center gap-1"
                          >
                            <div className="relative h-12 w-12 overflow-hidden rounded-lg bg-green-100 dark:bg-green-900/30">
                              {selfieImageUrls[SELFIE_LABELS[i]] ? (
                                <img
                                  src={selfieImageUrls[SELFIE_LABELS[i]]}
                                  alt={title}
                                  className="h-full w-full object-cover"
                                />
                              ) : selfies[i]?.previewUrl ? (
                                <img
                                  src={selfies[i].previewUrl}
                                  alt={title}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                  <MdCheckCircle className="h-5 w-5 text-green-500" />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteAndRecaptureSelfie(i)
                                }
                                className="absolute inset-0 flex items-center justify-center rounded-lg bg-red-500/80 opacity-0 transition-opacity group-hover:opacity-100"
                              >
                                <MdDelete className="h-4 w-4 text-white" />
                              </button>
                            </div>
                            <span className="text-[9px] text-ink-muted">
                              {title}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* Sequential camera viewfinder */
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-xs font-medium text-ink">
                          {t("firstLogin.selfieStepLabel", {
                            step: selfieStep + 1,
                            title: SELFIE_TITLES[selfieStep],
                          })}
                        </p>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              if (selfieStep > 0) {
                                stopSelfieCamera();
                                setSelfieCaptured(null);
                                setSelfieStep(selfieStep - 1);
                              }
                            }}
                            disabled={selfieStep === 0}
                            className="rounded p-1 text-ink-subtle hover:bg-surface-hover disabled:opacity-30"
                          >
                            <MdNavigateBefore className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (selfieStep < 2) {
                                stopSelfieCamera();
                                setSelfieCaptured(null);
                                setSelfieStep(selfieStep + 1);
                              }
                            }}
                            disabled={selfieStep === 2}
                            className="rounded p-1 text-ink-subtle hover:bg-surface-hover disabled:opacity-30"
                          >
                            <MdNavigateNext className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {selfieStatuses[selfieStep] === "done" ? (
                        <div className="flex flex-col items-center gap-2 rounded-xl bg-green-50 py-6 dark:bg-green-900/10">
                          {selfieImageUrls[SELFIE_LABELS[selfieStep]] ? (
                            <img
                              src={selfieImageUrls[SELFIE_LABELS[selfieStep]]}
                              alt={SELFIE_TITLES[selfieStep]}
                              className="h-28 w-28 rounded-xl object-cover shadow"
                            />
                          ) : selfies[selfieStep]?.previewUrl ? (
                            <img
                              src={selfies[selfieStep].previewUrl}
                              alt={SELFIE_TITLES[selfieStep]}
                              className="h-28 w-28 rounded-xl object-cover shadow"
                            />
                          ) : (
                            <MdCheckCircle className="h-10 w-10 text-green-500" />
                          )}
                          <p className="text-sm text-green-700 dark:text-green-400">
                            {t("firstLogin.selfieStepDone", {
                              title: SELFIE_TITLES[selfieStep],
                            })}
                          </p>
                          <button
                            type="button"
                            onClick={() =>
                              handleDeleteAndRecaptureSelfie(selfieStep)
                            }
                            className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                          >
                            <MdDelete className="h-3.5 w-3.5" />
                            {t("selfCheckin.retake")}
                          </button>
                        </div>
                      ) : (
                        <div className="bg-black overflow-hidden rounded-xl border-2 border-line">
                          <div className="ui-aspect-4-3 relative">
                            {selfieCameraError ? (
                              <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
                                <MdWarning className="h-8 w-8 text-red-400" />
                                <p className="text-xs text-red-400">
                                  {t(selfieCameraError)}
                                </p>
                                <button
                                  type="button"
                                  onClick={startSelfieCamera}
                                  className="flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1 text-xs text-white hover:bg-white/20"
                                >
                                  <MdRefresh className="h-3.5 w-3.5" />{" "}
                                  {t("action.retry")}
                                </button>
                              </div>
                            ) : selfieStatuses[selfieStep] === "uploading" ? (
                              <div className="flex h-full flex-col items-center justify-center gap-2">
                                <div className="border-t-transparent h-8 w-8 animate-spin rounded-full border-4 border-brand-500" />
                                <p className="text-xs text-white/70">
                                  {t("firstLogin.uploading")}
                                </p>
                              </div>
                            ) : selfieCaptured ? (
                              <img
                                src={selfieCaptured}
                                alt={t("firstLogin.capturedAlt")}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <>
                                <video
                                  ref={selfieVideoRef}
                                  autoPlay
                                  playsInline
                                  muted
                                  className="h-full w-full object-cover"
                                />
                                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                  <div className="h-48 w-36 rounded-full border-2 border-dashed border-white/50" />
                                </div>
                              </>
                            )}
                          </div>

                          <canvas ref={selfieCanvasRef} className="hidden" />

                          <div className="flex items-center justify-center gap-3 bg-gray-900 px-4 py-3">
                            {selfieStatuses[selfieStep] === "captured" &&
                            selfieCaptured ? (
                              <>
                                <button
                                  type="button"
                                  onClick={retakeSelfiePhoto}
                                  className="flex-1 rounded-lg border border-white/20 py-2 text-xs font-medium text-white hover:bg-white/10"
                                >
                                  {t("selfCheckin.retake")}
                                </button>
                                <button
                                  type="button"
                                  onClick={confirmAndUploadSelfie}
                                  className="flex-1 rounded-lg bg-brand-500 py-2 text-xs font-medium text-white hover:bg-brand-600"
                                >
                                  {t("firstLogin.confirmUpload")}
                                </button>
                              </>
                            ) : selfieStatuses[selfieStep] === "error" ? (
                              <button
                                type="button"
                                onClick={retakeSelfiePhoto}
                                className="flex-1 rounded-lg border border-white/20 py-2 text-xs font-medium text-white hover:bg-white/10"
                              >
                                {t("selfCheckin.retake")}
                              </button>
                            ) : selfieStatuses[selfieStep] === "uploading" ? (
                              <p className="text-xs text-white/50">
                                {t("firstLogin.processing")}
                              </p>
                            ) : !selfieCameraError ? (
                              <button
                                type="button"
                                onClick={captureSelfiePhoto}
                                className="h-12 w-12 rounded-full border-4 border-brand-500 bg-white shadow-md transition-transform hover:scale-105 active:scale-95"
                              >
                                <MdCameraAlt className="mx-auto h-5 w-5 text-brand-500" />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      )}

                      {selfieError && (
                        <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">
                          {t(selfieError)}
                        </div>
                      )}

                      <div className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                        {t("firstLogin.selfieTip")}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mt-5 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {t(error)}
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={goPrev}
                disabled={submitting}
                className="inline-flex items-center gap-1 rounded-lg border border-line px-3 py-2 text-sm text-ink hover:border-brand-500 disabled:opacity-60"
              >
                <MdChevronLeft className="h-4 w-4" /> {t("action.back")}
              </button>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            {step < totalSteps && (
              <button
                type="button"
                onClick={goNext}
                disabled={submitting}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {t("action.next")} <MdChevronRight className="h-4 w-4" />
              </button>
            )}

            {step === totalSteps && (
              <button
                type="button"
                onClick={() => activateAccount()}
                disabled={submitting}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
              >
                {submitting
                  ? t("firstLogin.processing")
                  : t("firstLogin.activate")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
